/**
 * Utility functions for importing and exporting data
 */

import i18n from '../../i18n';

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects with {key, label} format
 * @param {string} filename - Name for the downloaded file
 */
export const exportToCSV = (data, headers, filename) => {
  if (!data || !data.length) {
    alert(i18n.t('common.no_export_data'));
    return;
  }

  try {
    // Create CSV header row
    const headerRow = headers.map(h => `"${h.label}"`).join(',');
    
    // Create CSV data rows
    const csvRows = data.map(item => {
      return headers.map(header => {
        // Get the value using the key, handle nested properties with dot notation
        let value = header.key.split('.').reduce((obj, key) => {
          return obj ? obj[key] : '';
        }, item);
        
        // Handle special cases
        if (value === null || value === undefined) value = '';
        if (typeof value === 'object') value = JSON.stringify(value);
        
        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',');
    });
    
    // Combine header and data rows
    const csvContent = [headerRow, ...csvRows].join('\n');
    
    // Create download link
    const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename || 'export.csv');
    link.style.visibility = 'hidden';
    
    // Append to document, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error(i18n.t('common.log_export_csv_error'), error);
    alert(i18n.t('common.export_failed'));
    return false;
  }
};

/**
 * Export data to Excel format
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects with {key, label} format
 * @param {string} filename - Name for the downloaded file
 * @param {string} sheetName - Name for the Excel sheet
 */
export const exportToExcel = async (data, headers, filename, sheetName) => {
  const resolvedSheetName = sheetName || i18n.t('common.data_sheet');
  if (!data || !data.length) {
    alert(i18n.t('common.no_export_data'));
    return;
  }

  try {
    // Dynamically load the XLSX library
    const XLSX = await import('xlsx');
    
    // Prepare the data for Excel (array of arrays)
    const excelData = [
      // Header row
      headers.map(h => h.label),
      // Data rows
      ...data.map(item => {
        return headers.map(header => {
          // Get the value using the key, handle nested properties with dot notation
          let value = header.key.split('.').reduce((obj, key) => {
            return obj ? obj[key] : '';
          }, item);
          
          // Handle special cases
          if (value === null || value === undefined) value = '';
          return value;
        });
      })
    ];
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Create workbook and add the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, resolvedSheetName);
    
    // Generate Excel file and initiate download
    XLSX.writeFile(wb, filename || 'export.xlsx');
    
    return true;
  } catch (error) {
    console.error(i18n.t('common.log_export_excel_error'), error);
    
    // If the error is because the XLSX library is not loaded, provide a CSV fallback
    if (error.message.includes('XLSX is not defined') || error.code === 'MODULE_NOT_FOUND') {
      alert(i18n.t('common.excel_unavailable_csv_fallback'));
      return exportToCSV(data, headers, filename.replace('.xlsx', '.csv'));
    }
    
    alert(i18n.t('common.export_excel_error'));
    return false;
  }
};

/**
 * Parse CSV file content
 * @param {string} content - CSV content as string
 * @param {Array} headers - Array of header objects with {key, label} format for mapping
 * @returns {Array} Array of objects from CSV
 */
export const parseCSV = (content, headers) => {
  try {
    // Split content into rows
    const rows = content.split(/\\r?\\n/);
    if (rows.length < 2) throw new Error(i18n.t('common.csv_empty_invalid'));
    
    // Parse header row to match with our expected headers
    const fileHeaders = rows[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    
    // Create mapping from file headers to our keys
    const headerMap = {};
    fileHeaders.forEach((fileHeader, index) => {
      const matchedHeader = headers.find(h => 
        h.label.toLowerCase() === fileHeader.toLowerCase() ||
        h.key.toLowerCase() === fileHeader.toLowerCase()
      );
      
      if (matchedHeader) {
        headerMap[index] = matchedHeader.key;
      }
    });
    
    // Parse data rows
    const result = [];
    for (let i = 1; i < rows.length; i++) {
      if (!rows[i].trim()) continue; // Skip empty rows
      
      // Handle CSV escaping
      const re = /(?:,|\\n|^)("(?:(?:"")*[^"]*)*"|[^",\\n]*|(?:\\n|$))/g;
      const row = [];
      let match;
      
      while ((match = re.exec(rows[i])) !== null) {
        let value = match[1];
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.slice(1, -1).replace(/""/g, '"');
        }
        if (value.startsWith(',')) value = value.slice(1);
        row.push(value);
      }
      
      // Map to object with our keys
      const obj = {};
      Object.keys(headerMap).forEach(index => {
        const key = headerMap[index];
        const value = row[index] || '';
        
        // Handle nested properties with dot notation
        const keyParts = key.split('.');
        if (keyParts.length > 1) {
          let current = obj;
          for (let j = 0; j < keyParts.length - 1; j++) {
            const part = keyParts[j];
            current[part] = current[part] || {};
            current = current[part];
          }
          current[keyParts[keyParts.length - 1]] = value;
        } else {
          obj[key] = value;
        }
      });
      
      result.push(obj);
    }
    
    return result;
  } catch (error) {
    console.error(i18n.t('common.log_import_csv_parse'), error);
    throw error;
  }
};

/**
 * Parse Excel file content
 * @param {File} file - Excel file
 * @param {Array} headers - Array of header objects with {key, label} format for mapping
 * @returns {Promise<Array>} Promise resolving to array of objects from Excel
 */
export const parseExcel = async (file, headers) => {
  try {
    // Dynamically load the XLSX library
    const XLSX = await import('xlsx');
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          // Parse workbook
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to array of arrays
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (rows.length < 2) throw new Error(i18n.t('common.excel_empty_invalid'));
          
          // Parse header row to match with our expected headers
          const fileHeaders = rows[0];
          
          // Create mapping from file headers to our keys
          const headerMap = {};
          fileHeaders.forEach((fileHeader, index) => {
            const matchedHeader = headers.find(h => 
              h.label.toLowerCase() === String(fileHeader).toLowerCase() ||
              h.key.toLowerCase() === String(fileHeader).toLowerCase()
            );
            
            if (matchedHeader) {
              headerMap[index] = matchedHeader.key;
            }
          });
          
          // Parse data rows
          const result = [];
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (!row || !row.length) continue; // Skip empty rows
            
            // Map to object with our keys
            const obj = {};
            Object.keys(headerMap).forEach(index => {
              const key = headerMap[index];
              const value = row[index] || '';
              
              // Handle nested properties with dot notation
              const keyParts = key.split('.');
              if (keyParts.length > 1) {
                let current = obj;
                for (let j = 0; j < keyParts.length - 1; j++) {
                  const part = keyParts[j];
                  current[part] = current[part] || {};
                  current = current[part];
                }
                current[keyParts[keyParts.length - 1]] = value;
              } else {
                obj[key] = value;
              }
            });
            
            result.push(obj);
          }
          
          resolve(result);
        } catch (error) {
          console.error(i18n.t('common.log_import_excel_parse'), error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        console.error(i18n.t('common.log_import_excel_read'), error);
        reject(error);
      };
      
      reader.readAsArrayBuffer(file);
    });
  } catch (error) {
    console.error(i18n.t('common.log_import_excel_parse'), error);
    throw error;
  }
};

/**
 * Validate imported data against a schema
 * @param {Array} data - Array of objects to validate
 * @param {Object} schema - Validation schema with field requirements
 * @returns {Object} Object containing valid and invalid data arrays, and error messages
 */
export const validateImportData = (data, schema) => {
  const validData = [];
  const invalidData = [];
  const errors = [];
  
  data.forEach((item, index) => {
    const rowErrors = [];
    const rowNum = index + 2; // +2 because of 0-index and header row
    
    // Check required fields
    schema.requiredFields.forEach(field => {
      if (!item[field] && item[field] !== 0 && item[field] !== false) {
        rowErrors.push(i18n.t('common.import_required_field', { field }));
      }
    });
    
    // Validate specific fields
    if (schema.validators) {
      Object.keys(schema.validators).forEach(field => {
        if (item[field] !== undefined && item[field] !== '') {
          const validator = schema.validators[field];
          const isValid = validator(item[field]);
          if (!isValid) {
            rowErrors.push(i18n.t('common.import_invalid_value', { field, value: item[field] }));
          }
        }
      });
    }
    
    if (rowErrors.length > 0) {
      invalidData.push(item);
      errors.push(i18n.t('common.import_row_error', { row: rowNum, errors: rowErrors.join(', ') }));
    } else {
      validData.push(item);
    }
  });
  
  return { validData, invalidData, errors };
};

/**
 * Import data from a file (CSV or Excel)
 * @param {File} file - File object from input
 * @param {Array} headers - Array of header objects with {key, label} format
 * @param {Object} schema - Validation schema with field requirements
 * @returns {Promise<Object>} Promise resolving to import results
 */
export const importFromFile = async (file, headers, schema) => {
  try {
    const fileType = file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'excel';
    let data;
    
    if (fileType === 'csv') {
      const content = await file.text();
      data = parseCSV(content, headers);
    } else {
      data = await parseExcel(file, headers);
    }
    
    // Validate data
    const validation = validateImportData(data, schema);
    
    return {
      success: validation.validData.length > 0,
      data: validation.validData,
      invalidData: validation.invalidData,
      errors: validation.errors,
      totalCount: data.length,
      validCount: validation.validData.length,
      invalidCount: validation.invalidData.length
    };
  } catch (error) {
    console.error(i18n.t('common.log_import_error'), error);
    throw error;
  }
};

/**
 * Create a hidden file input and trigger click
 * @param {Function} onFileSelect - Callback when file is selected
 * @param {string} accept - Accept attribute for file input
 */
export const triggerFileInput = (onFileSelect, accept = '.csv,.xlsx,.xls') => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.style.display = 'none';
  
  input.onchange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect(file);
    }
    // Clean up
    document.body.removeChild(input);
  };
  
  document.body.appendChild(input);
  input.click();
};

